import React from "react";
import "./SettingsForm.css";
import { Formik } from "formik";
import * as Yup from "yup";
import { FormControlLabel, Switch } from "@mui/material";
import TextFieldInput from "../../../Atoms/Inputs/TextField/TextFieldInput";
import PrimaryButton from "../../../Atoms/Buttons/Primary/PrimaryButton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const SettingsSchema = Yup.object().shape({
  registrationDeadLine: Yup.date()
    .required("Registration deadline is required")
    .typeError("Invalid date"),
  editDeadLine: Yup.date()
    .required("Edit deadline is required")
    .typeError("Invalid date"),
  paymentDeadLine: Yup.date()
    .required("Payment deadline is required")
    .typeError("Invalid date"),
  registrationAbility: Yup.boolean().required(),
  editAbility: Yup.boolean().required(),
  paymentAbility: Yup.boolean().required(),
});

function SettingsForm({ onSubmit, onCancel, initialValues, loading = false }) {
  const formInitialValues = {
    registrationDeadLine: initialValues?.registrationDeadLine
      ? dayjs(initialValues.registrationDeadLine)
      : null,
    editDeadLine: initialValues?.editDeadLine
      ? dayjs(initialValues.editDeadLine)
      : null,
    paymentDeadLine: initialValues?.paymentDeadLine
      ? dayjs(initialValues.paymentDeadLine)
      : null,
    registrationAbility: initialValues?.registrationAbility ?? true,
    editAbility: initialValues?.editAbility ?? true,
    paymentAbility: initialValues?.paymentAbility ?? true,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Formik
        initialValues={formInitialValues}
        validationSchema={SettingsSchema}
        enableReinitialize={true}
        onSubmit={(values) => {
          const submitData = {
            registrationDeadLine: values.registrationDeadLine
              ? values.registrationDeadLine.toISOString()
              : null,
            editDeadLine: values.editDeadLine
              ? values.editDeadLine.toISOString()
              : null,
            paymentDeadLine: values.paymentDeadLine
              ? values.paymentDeadLine.toISOString()
              : null,
            registrationAbility: values.registrationAbility,
            editAbility: values.editAbility,
            paymentAbility: values.paymentAbility,
          };
          onSubmit(submitData);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form className="settingsForm" onSubmit={handleSubmit}>
            <div className="formSection" style={{ marginTop: "10px" }}>
              <h3 className="sectionTitle">Deadlines</h3>

              <div className="formField">
                <DatePicker
                  label="Registration Deadline"
                  value={values.registrationDeadLine}
                  onChange={(newValue) => {
                    setFieldValue("registrationDeadLine", newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        errors.registrationDeadLine &&
                        touched.registrationDeadLine,
                      helperText:
                        errors.registrationDeadLine &&
                        touched.registrationDeadLine
                          ? errors.registrationDeadLine
                          : "",
                    },
                  }}
                />
              </div>

              <div className="formField">
                <DatePicker
                  label="Edit Deadline"
                  value={values.editDeadLine}
                  onChange={(newValue) => {
                    setFieldValue("editDeadLine", newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: errors.editDeadLine && touched.editDeadLine,
                      helperText:
                        errors.editDeadLine && touched.editDeadLine
                          ? errors.editDeadLine
                          : "",
                    },
                  }}
                />
              </div>

              <div className="formField">
                <DatePicker
                  label="Payment Deadline"
                  value={values.paymentDeadLine}
                  onChange={(newValue) => {
                    setFieldValue("paymentDeadLine", newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: errors.paymentDeadLine && touched.paymentDeadLine,
                      helperText:
                        errors.paymentDeadLine && touched.paymentDeadLine
                          ? errors.paymentDeadLine
                          : "",
                    },
                  }}
                />
              </div>
            </div>

            <div className="formSection">
              <h3 className="sectionTitle">Abilities</h3>

              <div className="formField">
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.registrationAbility}
                      onChange={(e) =>
                        setFieldValue("registrationAbility", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Registration Ability"
                />
              </div>

              <div className="formField">
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.editAbility}
                      onChange={(e) =>
                        setFieldValue("editAbility", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Edit Ability"
                />
              </div>

              <div className="formField">
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.paymentAbility}
                      onChange={(e) =>
                        setFieldValue("paymentAbility", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Payment Ability"
                />
              </div>
            </div>

            <div className="formActions">
              <PrimaryButton
                classBtn={"center cancelButton"}
                text={"Cancel"}
                style={{
                  textTransform: "capitalize",
                  borderRadius: "5px",
                  color: "var(--primary)",
                  backgroundColor: "white",
                  border: "1px solid var(--primary)",
                  height: "40px",
                  padding: "0 20px",
                  minWidth: "100px",
                }}
                click={(e) => {
                  e.preventDefault();
                  onCancel();
                }}
              />
              <PrimaryButton
                classBtn={"center submitButton"}
                text={"Update Settings"}
                type="submit"
                loading={loading}
                disabled={loading}
                style={{
                  textTransform: "capitalize",
                  borderRadius: "5px",
                  color: "white",
                  backgroundColor: "var(--primary)",
                  height: "40px",
                  padding: "0 20px",
                  minWidth: "140px",
                }}
              />
            </div>
          </form>
        )}
      </Formik>
    </LocalizationProvider>
  );
}

export default SettingsForm;
